import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { RoLockedView, roLockOf } from '../../../components/billing/RoLocked'
import { useHistory, useLocation } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { ArrowLeft, Calendar, Car, CheckCircle2, ChevronDown, ChevronUp, FileText, HelpCircle, Mail, Wrench } from 'lucide-react'

import { useAuthStore } from '@caradasstore/AuthStore'
import { AssigneeAvatarPicker } from '@components/assigned-user'

import { BrandedModal } from '@components/branded-modal'
import { CarImageHolder } from '@components/car-image-holder'
import { CustomItem } from '@components/custom-item'
import { Drawers } from '@components/drawers'
import { EmailForm } from '@components/email'
import { Note } from '@components/note'
import { VinInput } from '@components/vin'
import {
  AdasAlert,
  AdasBox,
  AdasButton,
  AdasContainer,
  AdasImage,
  AdasModal,
  AdasPaper,
  AdasTextField,
  AdasTypography
} from '@components/wrapper-components'
import {
  closeQuote,
  getCalibrationList,
  getQuote,
  getQuoteEmails,
  getQuotePdf,
  getQuoteByReferenceNumber,
  getServiceList,
  transformToWO,
  updateNote,
  updateQuote,
  updateWorkOrderCalibrations,
  updateWorkOrderServices,
  getQWIByQuoteId,
  updateClient,
  updateWorkOrderParts,
  updateWorkOrderLabour,
  getPartsList,
  getLabourList,
  getReferralByCode
} from '../../../api/api'
import { PARAM_NAMES, getUrlByNameAndId, getWorkOrderSummaryUrl, REDIRECT_URLS } from '../../../constants'
import { useLoadingStore, useMessageStore } from '../../../store'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter } from '../../../utils/utils'
import { StatusEnum, SubStatusEnum } from 'enums/enums'
import { CreateAppointmentForm } from '@components/scheduler/schedule-calendar/CreateAppointmentForm'
import { buildAppointmentPrefillFromQuote } from '@components/scheduler/schedule-calendar/helper'
import { useExistingAppointmentGuard } from '@hooks/useExistingAppointmentGuard'
import { Logger } from '../../../logger'
import { PdfActionMenuItems, pdfActionItems } from '@components/common'
import { handlePdf, PDF_ACTION } from '../../../utils/pdfActions'
import ExpandableSection from '@components/custom-item/item-list/ExpandableSection'
import { MobileSection } from '@components/custom-item/item-list/MobileSection'
import { EditProductPricing } from '@components/product/edit-product-pricing/EditProductPricing'
import { useEditPricing } from '../../../hooks/useEditPricing'
import { handleDrawerClose } from '../../../hooks/handleDrawerClose'
import { flattenItem, normalizeCustomItems, splitCustomItems } from '../../../utils/itemHelpers'
import { MAX_DESCRIPTION_LENGTH } from '../../../constants'
import { itemHandlersFactory } from 'utils/populateItemsUtil'
import { ProductContractBanner, hasContractBanner } from '@components/product'
import { CalibrationLevelLegend, shouldShowLevelLegend } from '@components/calibration-level-legend'
import { CreateSupportRequestModal } from '@pages/support/support-request-modal'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { EstimateChangeBanner } from '@components/quote-estimate-change'
import { useModuleAccess } from '@hooks/useModuleAccess'
export const QuoteSummaryPage = () => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const [packages, setPackages] = useState([])
  const [roLock, setRoLock] = useState(null) // the plan's RO lock refusal, when the quote is locked
  const [services, setServices] = useState([])
  const [parts, setParts] = useState([])
  const [labor, setLabor] = useState([])
  const [availableCalibrations, setAvailableCalibrations] = useState() // previously allCalibrations
  const [availableServices, setAvailableServices] = useState() // previously allServices
  const [availableParts, setAvailableParts] = useState() // previously allParts
  const [availableLabour, setAvailableLabour] = useState() // previously allLabour
  const [supportOpen, setSupportOpen] = useState(false)
  // CAR-3678 — New Appointment modal, prefilled + linked to this quote.
  const [apptOpen, setApptOpen] = useState(false)
  // CAR-4034 — New Appointment first checks the quote isn't already booked (confirm dialog if it is).
  const openAppointmentForm = useCallback(() => setApptOpen(true), [])
  const { requestNewAppointment, guardDialog: existingAppointmentDialog } = useExistingAppointmentGuard(openAppointmentForm)
  const [isCalibrationOpen, setIsCalibrationOpen] = useState(false)
  const [isServiceOpen, setIsServiceOpen] = useState(false)
  const [isPartsOpen, setIsPartsOpen] = useState(false)
  const [isLabourOpen, setIsLabourOpen] = useState(false)

  const setLoading = useLoadingStore((store) => store.setLoading)
  const addMessage = useMessageStore((store) => store.addMessage)
  const authCtx = useAuthStore()
  const [quoteData, setQuoteData] = useState({})
  const [quoteInputValues, setQuoteInputValues] = useState({
    notes_external: quoteData.notes_external || '',
    notes_internal: quoteData.notes_internal || '',
    client_notes: quoteData.client?.notes || ''
  })

  const [closeInput, setCloseInput] = useState('')
  const [formData, setFormData] = useState({})
  const [isVinEdit, setIsVinEdit] = useState(false)
  const [isEmail, setIsEmail] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [quotePdf, setQuotePdf] = useState()
  const [open, setOpen] = useState(false)
  const [updatedVehicle, setUpdatedVehicle] = useState()
  const [isVinInvalid, setIsVinInvalid] = useState(false)
  const [customItemList, setCustomItemList] = useState([])
  const [QWIData, setQWIData] = useState(undefined)
  const [referralData, setReferralData] = useState()
  const textDisplay = { textAlign: 'center' }
  const history = useHistory()
  const location = useLocation()

  const redirectWO = (data) => {
    history.replace(getWorkOrderSummaryUrl(data?.reference_number))
  }
  const redirectToForbiddenPage = () => {
    history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
  }

  const onCloseModal = () => {
    setUpdatedVehicle(null)
    setIsVinEdit(false)
    setIsVinInvalid(false)
  }

  const packagesTotal = packages.length > 0 ? packages.reduce((total, current) => total + current.total_price, 0) : 0

  const servicesTotal = services.length > 0 ? services.reduce((total, current) => total + current.total_price, 0) : 0
  const partsTotal = parts.length > 0 ? parts.reduce((total, current) => total + current.total_price, 0) : 0
  const laborTotal = labor.length > 0 ? labor.reduce((total, current) => total + current.total_price, 0) : 0

  const getCustomItemTotal = useCallback(
    (items = []) =>
      items.length > 0
        ? items.reduce((total_price, current) => {
            if (current.type === 'DISCOUNT') {
              return total_price - current.total_price
            }
            return total_price + current.total_price
          }, 0)
        : 0,
    []
  )

  const customItemTotal = useMemo(() => getCustomItemTotal(customItemList), [customItemList, getCustomItemTotal])
  // CAR-3893: the client rebate is only shown on invoices; a quote totals to the figure before it.
  const totalCost = packagesTotal + servicesTotal + customItemTotal + partsTotal + laborTotal

  const handleConfirmClose = async () => {
    const closeQuoteResponse = closeQuote({
      quoteId: quoteData.id
    })
    closeQuoteResponse
      .then(() => {
        history.push({
          pathname: '/quotes',
          search: `?status=${quoteData.status || 'OPEN'}`
        })
      })
      .catch((error) => console.log(error))
  }
  const handleOpen = () => {
    setOpen(true)
  }

  const onCloseCalibrations = (data) =>
    handleDrawerClose({
      setLoading: () => {}, // or remove if you don't use loading
      setDrawerOpen: setIsCalibrationOpen,
      updateFn: updateWorkOrderCalibrations,
      setItems: setPackages,
      sourceData: quoteData,
      data,
      logFile: 'updateSelectedCalibration',
      itemKey: 'calibrations',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseServices = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsServiceOpen,
      updateFn: updateWorkOrderServices,
      setItems: setServices,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderServices',
      itemKey: 'services',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseParts = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsPartsOpen,
      updateFn: updateWorkOrderParts,
      setItems: setParts,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderParts',
      itemKey: 'parts',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseLabour = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsLabourOpen,
      updateFn: updateWorkOrderLabour,
      setItems: setLabor,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderLabour',
      itemKey: 'labors',
      type: 'quotes',
      onSuccess: fetchData
    })

  const handleClose = () => {
    setOpen(false)
    setCloseInput('')
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const loadWorkOrderData = async () => {
        const response = await getQuote({
          quoteId: quoteData.id
        })
        if (response?.status !== 200) {
          redirectToForbiddenPage()
          return
        }
        const { data } = response
        const { quote } = data

        // Attach additional data to workOrder
        Object.assign(quote, {
          safetySystemsItems: data.safetySystemsItems,
          calibrations: data.qwiCalibrations,
          services: data.qwiServices,
          parts: data.qwiParts,
          labors: data.qwiLabours,
          customItems: data.qwiCustomItems
        })

        setQuoteData(quote)
        setFormData(quote)
        const { notes_external, notes_internal, client } = quote
        setQuoteInputValues((prev) => ({
          ...prev,
          ...(notes_external && { notes_external }),
          ...(notes_internal && { notes_internal }),
          ...(client?.notes && { client_notes: client.notes })
        }))

        const selectedItemNums = {
          calibrations: [],
          services: [],
          parts: [],
          labours: []
        }

        if (data.qwiCalibrations?.length) {
          const flattened = data.qwiCalibrations.map((item) => flattenItem(item, 'calibration'))
          setPackages(flattened)
          selectedItemNums.calibrations = flattened.map((item) => item.itemNum)
        }

        if (data.qwiServices?.length) {
          const flattened = data.qwiServices.map((item) => flattenItem(item, 'service'))
          setServices(flattened)
          selectedItemNums.services = flattened.map((item) => item.itemNum)
        }

        if (data.qwiParts?.length) {
          const flattened = data.qwiParts.map((item) => flattenItem(item, 'part'))
          setParts(flattened)
          selectedItemNums.parts = flattened.map((item) => item.itemNum)
        }

        if (data.qwiLabours?.length) {
          const flattened = data.qwiLabours.map((item) => flattenItem(item, 'labour'))
          setLabor(flattened)
          selectedItemNums.labours = flattened.map((item) => item.itemNum)
        }

        if (data.qwiCustomItems?.length) {
          const updatedList = normalizeCustomItems(data.qwiCustomItems)
          // The client rebate line is left out: only invoices show it (CAR-3893).
          setCustomItemList(splitCustomItems(updatedList).standardItems)
        } else {
          setCustomItemList([])
        }

        // await populateSelectableItems(selectedItemNums)
      }
      await loadWorkOrderData()
    } catch (error) {
      // The quote's repair order is locked by the plan's monthly RO limit (403 RO_LOCKED, 0173): show the lock.
      const lock = roLockOf(error)
      if (lock) {
        setRoLock(lock)
        return
      }
      Logger.error({
        message: error,
        payload: { ccid: quoteData.calibrationCenter?.id, effectiveDate: quoteData.created_at, quoteId: quoteData.id }
      })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (quoteData?.id) fetchData()
  }, [quoteData?.quoteCreatedAt, quoteData?.calibrationCenter?.id, quoteData?.created_at])

  useEffect(() => {
    const fetchQuoteDataByReferenceNumber = async () => {
      if (!referenceNumber) return

      try {
        setLoading(true)
        const response = await getQuoteByReferenceNumber({ referenceNumber })
        if (response?.status === 200) {
          const data = response.data || {}
          data.quote.safetySystemsItems = data.safetySystemsItems || []
          setQuoteData(data.quote)
          const QWIResponse = await getQWIByQuoteId(data.quote.id)
          setQWIData(QWIResponse?.data)
          if (QWIResponse?.data.invoiceId || QWIResponse?.data.workorderId) {
            history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
          }
        } else {
          //Given referenceNumber doesnot exist or no permission granted
          redirectToForbiddenPage()
          return
        }
      } catch (error) {
        Logger.error({
          message: error,
          payload: { referenceNumber }
        })
      } finally {
        setLoading(false)
      }
    }

    fetchQuoteDataByReferenceNumber()
  }, [referenceNumber])

  const {
    selectedProduct,
    setSelectedProduct,
    isOpenEditProduct,
    setIsOpenEditProduct,
    handleClickEditPricing,
    handleEditPricingInputChange,
    handleEditPricingSubmit
  } = useEditPricing(fetchData)

  const saveNote = useCallback(
    async (notes) => {
      const isSuccess = (status) => status === 200 || status === 201

      if (notes.noteType === 'client_note') {
        const clientNotes = notes?.note?.client_notes
        const updatedClientData = {
          ...quoteData.client,
          notes: clientNotes
        }

        const { status, data } = await updateClient({ data: updatedClientData })

        if (isSuccess(status)) {
          setQuoteInputValues((prevNotes) => ({
            ...prevNotes,
            client_notes: data.notes
          }))
        }
      } else {
        const { status, data } = await updateNote({
          type: 'quotes',
          id: quoteData.id,
          data: notes
        })

        if (isSuccess(status)) {
          const updatedQuote = data.quote

          setQuoteInputValues((prevNotes) => ({
            ...prevNotes,
            notes_external: updatedQuote.notes_external,
            notes_internal: updatedQuote.notes_internal,
            client_notes: quoteData.client?.notes
          }))
        }
      }
    },
    [quoteData.id, quoteData.client]
  )

  const convertToWoUpdateVin = async () => {
    // eslint-disable-next-line no-unused-vars
    const { safetySystems, color, imageUrl, fuel, ...vehicle } = updatedVehicle
    const submitData = {
      ...formData,
      calibration_center_id: formData.calibrationCenter.id,
      client_id: formData.client.id,
      quote: false,
      insurance_company_id: formData.insurance_company?.id || null,
      vehicle: {
        calibration_center_id: authCtx.user, //change required
        exterior_color: color,
        image_url: imageUrl,
        fuel_capacity_gal: fuel,
        ...vehicle
      },
      safetySystems: updatedVehicle.safetySystems.map((item) => {
        return item.name
      })
    }
    const params = {
      data: submitData,
      quoteId: quoteData.id
    }

    try {
      const response = await updateQuote(params)
      if (response.status === 200 || response.status === 201) {
        const converWoResponse = await transformToWO(params)
        if (converWoResponse.status === 200 || converWoResponse.status === 201) {
          redirectWO({ reference_number: submitData.reference_number })
        }
      }
    } catch (error) {
      // e.g. 409: the vehicle is already in progress on another quote, work order or invoice.
      addMessage({ text: getFriendlyErrorMessage(error) })
    }
  }

  const convertToWO = async () => {
    const submitData = {
      ...formData,
      calibration_center_id: formData.calibrationCenter.id,
      client_id: formData.client.id,
      quote: false
    }

    const params = {
      data: submitData,
      quoteId: quoteData.id
    }
    if (!formData.vehicle.vin) {
      setIsVinEdit(true)
    } else {
      try {
        const response = await transformToWO(params)
        if (response.status === 200 || response.status === 201) {
          redirectWO({ reference_number: submitData.reference_number })
        }
      } catch (error) {
        // e.g. 409: the vehicle is already in progress on another quote, work order or invoice.
        addMessage({ text: getFriendlyErrorMessage(error) })
      }
    }
  }

  const updateVehicleInfo = (props) => {
    if (props.data?.make) {
      const updatedVehicle = {
        ...props.data,
        vin: props.vin
      }
      setUpdatedVehicle(updatedVehicle)
      setIsVinInvalid(false)
    } else {
      setUpdatedVehicle(null)
      setIsVinInvalid(true)
    }
  }

  const emailQuoteHandler = useCallback(async () => {
    const params = {
      quoteId: quoteData.id
    }

    setLoading(true)
    try {
      const emailContactResponse = await getQuoteEmails(params)
      if (emailContactResponse.status === 200) {
        setIsEmail(true)
        setEmailContacts(emailContactResponse.data)
      }
      const quotePdfResponse = await getQuotePdf({
        quoteId: quoteData?.id
      })
      if (quotePdfResponse.status === 200) {
        const fileValue = new File([quotePdfResponse.data], `quote_${quoteData?.reference_number}.pdf`)
        fileValue.id = quoteData?.id
        setQuotePdf(fileValue)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { QuoteId: quoteData.id, path: 'QuoteSummaryPage/emailQuoteHandler' } })
    } finally {
      setLoading(false)
    }
  }, [quoteData])

  // View, print or download the quote PDF (all users, client users included).
  const quotePdfHandler = useCallback(
    async (action = PDF_ACTION.VIEW) => {
      if (!quoteData?.id) return
      try {
        setLoading(true)
        const quotePdfResponse = await getQuotePdf({
          quoteId: quoteData.id
        })
        if (quotePdfResponse.status === 200) {
          handlePdf(quotePdfResponse.data, `quote_${quoteData.reference_number}.pdf`, action)
        }
      } catch (error) {
        Logger.error({ message: error, payload: { QuoteId: quoteData.id, action, path: 'QuoteSummaryPage/quotePdfHandler' } })
      } finally {
        setLoading(false)
      }
    },
    [quoteData]
  )

  const vehicleData = quoteData?.vehicle || {}
  const isActive = StatusEnum[quoteData?.status] === StatusEnum.OPEN
  // isActive still means "the quote is open" and keeps gating the read actions; canEdit
  // is what the write controls hang off, so a client user sees an open quote exactly as
  // a closed one: every pencil, add and convert is inert, the PDF still opens.
  const isReadOnly = useIsReadOnly()
  const canEdit = isActive && !isReadOnly
  // Quotes and work orders are separate features: converting needs a plan that has work orders.
  const canConvertToWorkOrder = useModuleAccess()('work_orders')
  const isServiceOnly = quoteData?.type === 'SERVICE_ONLY'
  // National Agreement quotes carry a referral source code — their line items are
  // read-only for every role (view-only sheet, view icon instead of the edit pencil).
  const isNationalAgreement = !!QWIData?.referralSourceCode

  const [actionsOpen, setActionsOpen] = useState(false)
  const actionsRef = useRef(null)
  // The mobile menu is registered once per dependency change, so it calls the latest convertToWO through a ref.
  const convertToWORef = useRef(convertToWO)
  convertToWORef.current = convertToWO
  // The toolbar effect below doesn't re-run on quote changes; read the latest quote at click time.
  const newAppointmentRef = useRef()
  newAppointmentRef.current = () => requestNewAppointment(quoteData)
  useEffect(() => {
    if (!actionsOpen) return
    const handler = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [actionsOpen])

  // Register the Actions menu with the global mobile header so it replaces the
  // search/filter icons on this page. Items mirror the desktop dropdown.
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)
  useEffect(() => {
    setPageAction({
      label: 'Actions',
      // Left on isActive, not canEdit: the menu still holds View / Print / Download, which read.
      disabled: !isActive,
      // Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, page steps, Close, Support.
      items: [
        ...(isReadOnly ? [] : [{ label: 'Email Quote', icon: Mail, onClick: () => emailQuoteHandler() }]),
        ...pdfActionItems('Quote', quotePdfHandler),
        ...(canEdit && canConvertToWorkOrder
          ? [{ label: 'Create Work Order', icon: Wrench, onClick: () => convertToWORef.current() }]
          : []),
        // Booking an appointment is the one write a client user may make.
        { label: 'New Appointment', icon: Calendar, onClick: () => newAppointmentRef.current() },
        ...(canEdit ? [{ label: 'Close Quote', icon: CheckCircle2, onClick: () => setOpen(true) }] : []),
        ...(isReadOnly ? [] : [{ label: 'Support', icon: HelpCircle, onClick: () => setSupportOpen(true) }])
      ]
    })
    return () => clearPageAction()
  }, [
    isActive,
    isReadOnly,
    canEdit,
    canConvertToWorkOrder,
    emailQuoteHandler,
    quotePdfHandler,
    history,
    referenceNumber,
    setPageAction,
    clearPageAction
  ])

  // The Quotes dashboard sends a quote with no VIN here to convert it: the VIN prompt lives on this page.
  const convertRequested = Boolean(location.state?.convertToWorkOrder)
  useEffect(() => {
    if (!convertRequested || !formData?.id || !canEdit || !canConvertToWorkOrder) return
    history.replace({ ...location, state: {} })
    convertToWO()
  }, [convertRequested, formData?.id, canEdit, canConvertToWorkOrder])

  const handleClickClosePricing = (type) => {
    setIsOpenEditProduct((prev) => ({ ...prev, [type]: false }))
    setSelectedProduct({})
  }
  const onSaveCustomItem = (items) => {
    const normalizedItems = normalizeCustomItems(items)
    setCustomItemList(splitCustomItems(normalizedItems).standardItems)
    fetchData()
  }

  const { populateSelectableItems } = itemHandlersFactory({
    getCalibrationList,
    getServiceList,
    getPartsList,
    getLabourList,
    setAvailableCalibrations,
    setAvailableServices,
    setAvailableParts,
    setAvailableLabour
  })
  const drawerSetters = {
    calibrations: setIsCalibrationOpen,
    services: setIsServiceOpen,
    parts: setIsPartsOpen,
    labor: setIsLabourOpen
  }

  // The Store / Auto Quote class tabs in Manage Calibrations/Services only apply to quotes
  // created by ADAS Intelligence auto-quote. Manually-created QWIs have no auto-quote pricing
  // profile, so they get Store products only (no Auto Quote tab). National-agreement quotes
  // (referral present) keep their existing tab-less behavior.
  const isAutoQuoteQwi = quoteData?.source === 'ADAS_INTELLIGENCE'

  const drawerTitles = {
    calibrations: 'Calibrations',
    services: 'Services',
    parts: 'Parts',
    labor: 'Labor'
  }

  // National-agreement quotes must use the referral catalog. If the referral hasn't loaded yet
  // (the effect below is still in flight), load it now rather than fall back to the centre catalog.
  const resolveReferral = async () => {
    if (referralData?.id || !QWIData?.referralSourceCode) return referralData
    const response = await getReferralByCode({
      ccId: quoteData?.calibrationCenter?.id,
      code: QWIData.referralSourceCode
    })
    if (response?.data?.id) setReferralData(response.data)
    return response?.data
  }

  const handleManageClick = async (type) => {
    const ccid = quoteData?.calibrationCenter?.id
    const qwi_id = QWIData?.id
    const referral = type !== 'parts' ? await resolveReferral() : referralData
    const showLoadError = () => addMessage({ text: `Couldn't load the ${drawerTitles[type]} list. Nothing was changed, please try again.` })
    if (type !== 'parts' && QWIData?.referralSourceCode && !referral?.id) {
      showLoadError()
      return
    }

    // Tick the lines the close handler compares against (`quoteData[itemKey]`), so an unchanged
    // drawer always closes with the same product ids and never saves.
    const selected = {
      calibrations: (quoteData?.calibrations || []).map((item) => flattenItem(item, 'calibration')),
      services: (quoteData?.services || []).map((item) => flattenItem(item, 'service')),
      parts: (quoteData?.parts || []).map((item) => flattenItem(item, 'part')),
      labours: (quoteData?.labors || []).map((item) => flattenItem(item, 'labour'))
    }

    let extraParams = {}

    if (type !== 'parts' && referral?.id) {
      extraParams = {
        belongsTo: 'REFERRAL',
        belongsToId: referral.id
      }
    }

    // Never open the drawer on a failed catalog load: closing it would save a list built from nothing.
    const loaded = await populateSelectableItems(type, ccid, qwi_id, selected, extraParams)
    if (!loaded) {
      showLoadError()
      return
    }
    drawerSetters[type]?.(true)
  }

  useEffect(() => {
    if (QWIData?.referralSourceCode) {
      const fetchReferralData = async () => {
        // Fetch referral data if referralSourceCode is present
        const referral_response = await getReferralByCode({
          ccId: quoteData?.calibrationCenter?.id,
          code: QWIData?.referralSourceCode
        })
        if (referral_response?.data) setReferralData(referral_response.data)
      }
      fetchReferralData()
    }
  }, [QWIData?.referralSourceCode, quoteData?.calibrationCenter?.id])

  if (roLock) {
    return <RoLockedView refusal={roLock} onBack={() => history.push('/quotes')} />
  }

  return (
    <AdasContainer maxWidth={false}>
      {quoteData.id && (
        <AdasBox className='mb-[60px] mt-5'>
          <EmailForm
            emailType='quote'
            quotePdf={quotePdf}
            isOpen={isEmail}
            contacts={emailContacts}
            emailData={quoteData}
            onClose={() => setIsEmail(false)}
          />
          <Drawers
            data={availableCalibrations}
            isOpen={isCalibrationOpen}
            onClose={onCloseCalibrations}
            title={'Calibrations'}
            enableProductClassTabs={!referralData?.id && isAutoQuoteQwi}
          />
          <Drawers
            data={availableServices}
            isOpen={isServiceOpen}
            onClose={onCloseServices}
            title={'Service'}
            enableProductClassTabs={!referralData?.id && isAutoQuoteQwi}
          />
          <Drawers data={availableParts} isOpen={isPartsOpen} onClose={onCloseParts} title={'Parts'} />
          <Drawers data={availableLabour} isOpen={isLabourOpen} onClose={onCloseLabour} title={'Labor'} />

          {/* A newer version of the vehicle's estimate updated (or would update) this quote. */}
          <EstimateChangeBanner quoteId={quoteData.id} readOnly={isReadOnly} onApplied={fetchData} />

          {/* Mobile vehicle card — meta + badge inside card, image, then title/total/client */}
          <AdasBox className='mb-3 min-[435px]:hidden rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='mb-3 flex items-center justify-between gap-3'>
              <AdasTypography className='text-[12px] text-[#2C3D4D]'>
                <span>Q# {quoteData.reference_number}</span>
                {quoteData.repair_order_num && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>RO# {quoteData.repair_order_num}</span>
                  </>
                )}
              </AdasTypography>
              <span
                className={`shrink-0 rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  isServiceOnly ? 'bg-[#FEF3C7] text-[#B45309]' : 'bg-[#DBEAFE] text-[#1F6FEB]'
                }`}
              >
                {isServiceOnly ? 'Service-Only' : 'Standard'}
              </span>
            </AdasBox>
            <AdasBox className='flex h-[140px] items-center justify-center overflow-hidden rounded-md bg-white text-[#6B7280]'>
              {vehicleData?.image_url ? (
                <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-contain' />
              ) : (
                <CarImageHolder showText={false} />
              )}
            </AdasBox>
            <AdasBox className='mt-3 text-center'>
              <AdasTypography className='text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                {vehicleData?.year
                  ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                  : 'Vehicle'}
              </AdasTypography>
              <AdasTypography className='mt-1 text-[14px] font-semibold text-[#0A1628]'>
                Total: {currencyFormatter(totalCost)}
              </AdasTypography>
              <AdasTypography className='mt-0.5 text-[12px] text-[#2C3D4D]'>
                {quoteData.client?.name && <span>{quoteData.client.name}</span>}
                {quoteData.insurance_company?.name && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>{quoteData.insurance_company.name}</span>
                  </>
                )}
              </AdasTypography>
              {quoteData.created_at && (
                <AdasTypography className='mt-1 text-[11px] text-[#2C3D4D]'>
                  Created{' '}
                  {(() => {
                    const d = new Date(quoteData.created_at)
                    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                    return `${date} ${time}`
                  })()}
                </AdasTypography>
              )}
              <AdasBox className='mt-2 flex justify-center' onClick={(e) => e.stopPropagation()}>
                <AssigneeAvatarPicker
                  type='quotes'
                  workOrderId={quoteData.id}
                  initialAssignee={quoteData.assigned_to}
                  closedStatus={!canEdit}
                  showName
                  showEditIcon
                />
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {/* Page heading — Back link + vehicle title + top-right action buttons */}
          <AdasBox className='mb-4 hidden min-[435px]:flex flex-wrap items-end justify-between gap-4'>
            <AdasBox>
              <button
                type='button'
                onClick={() => history.goBack()}
                className='mb-1 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                Back to Quotes
              </button>
              <h1 className='text-[28px] font-bold leading-tight text-[#0A1628]'>Quotes Summary</h1>
            </AdasBox>
            <AdasBox className='flex items-center gap-3'>
              <AssigneeAvatarPicker
                type='quotes'
                workOrderId={quoteData.id}
                initialAssignee={quoteData.assigned_to}
                closedStatus={!canEdit}
                showName
                label='Assignee:'
              />
              <AdasBox ref={actionsRef} className='relative'>
                <button
                  type='button'
                  onClick={() => setActionsOpen((o) => !o)}
                  disabled={!isActive}
                  className='inline-flex items-center gap-2 rounded-lg bg-[#0A1628] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  Actions
                  {actionsOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                </button>
                {actionsOpen && (
                  <AdasBox className='absolute right-0 top-full z-30 mt-2 w-[240px] overflow-hidden rounded-lg border border-[#EBEDF0] bg-white py-2 shadow-lg'>
                    {/* Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, page steps, Close, Support. */}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          emailQuoteHandler()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-foreground transition-colors hover:bg-[#F8FAFC]'
                      >
                        <Mail className='h-4 w-4 text-[#475569]' />
                        Email Quote
                      </button>
                    )}
                    <PdfActionMenuItems noun='Quote' onAction={quotePdfHandler} onClose={() => setActionsOpen(false)} />
                    {canEdit && canConvertToWorkOrder && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          convertToWO()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <Wrench className='h-4 w-4 text-[#475569]' />
                        Create Work Order
                      </button>
                    )}
                    {/* Booking an appointment is the one write a client user may make. */}
                    <button
                      type='button'
                      onClick={() => {
                        setActionsOpen(false)
                        requestNewAppointment(quoteData)
                      }}
                      className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                    >
                      <Calendar className='h-4 w-4 text-[#475569]' />
                      New Appointment
                    </button>
                    {canEdit && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          handleOpen()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <CheckCircle2 className='h-4 w-4 text-[#475569]' />
                        Close Quote
                      </button>
                    )}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          setSupportOpen(true)
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                      >
                        <HelpCircle className='h-4 w-4 text-[#475569]' />
                        Support
                      </button>
                    )}
                  </AdasBox>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {/* Vehicle summary card — thumbnail · title/VIN/client+insurance · Quote#/RO#/date */}
          <AdasBox className='mb-4 hidden min-[435px]:block rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='flex items-stretch justify-between gap-4'>
              <AdasBox className='flex items-start gap-4'>
                <AdasBox className='flex h-[100px] w-[160px] flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-[#EBEDF0] text-[#6B7280]'>
                  {vehicleData?.image_url ? (
                    <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-cover' />
                  ) : (
                    <CarImageHolder showText={false} />
                  )}
                </AdasBox>
                <AdasBox className='min-w-0 flex-1'>
                  <AdasTypography className='text-[18px] font-bold leading-tight text-[#0A1628]'>
                    {vehicleData?.year
                      ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                      : 'Vehicle'}
                  </AdasTypography>
                  {vehicleData?.vin && (
                    <AdasTypography className='mt-2 font-mono text-[13px] tracking-wider text-[#2C3D4D]'>{vehicleData.vin}</AdasTypography>
                  )}
                  <AdasBox className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#2C3D4D]'>
                    {quoteData.client?.name && <span>{quoteData.client.name}</span>}
                    {quoteData.insurance_company?.name && (
                      <>
                        <span className='text-[#D1D5D8]'>·</span>
                        <span>{quoteData.insurance_company.name}</span>
                      </>
                    )}
                  </AdasBox>
                </AdasBox>
              </AdasBox>
              <AdasBox className='flex flex-col items-end whitespace-nowrap'>
                <AdasTypography className='text-[14px] leading-tight text-[#2C3D4D]'>
                  <span>Quote# {quoteData.reference_number}</span>
                  {quoteData.repair_order_num && (
                    <>
                      <span className='mx-2 text-[#CBD5E1]'>|</span>
                      <span>RO# {quoteData.repair_order_num}</span>
                    </>
                  )}
                </AdasTypography>
                {/* Spacer matches the VIN row height on the left so the date lines up with the client row */}
                <AdasBox className='mt-2 h-[18px]' aria-hidden='true' />
                {quoteData.created_at && (
                  <AdasTypography className='mt-2 flex h-7 items-center text-[13px] text-[#2C3D4D]'>
                    {(() => {
                      const d = new Date(quoteData.created_at)
                      const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      return `${date} ${time}`
                    })()}
                  </AdasTypography>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          <AdasBox>
            {QWIData?.referralSourceCode && <ProductContractBanner referralData={referralData} />}
            {/* Static key for the Level 1 / 2 / 3A / 3B / 3C codes in calibration names, styled like the
                NA banner above. Shown only when the record has an Auto Quote line and that banner isn't shown (CAR-4019). */}
            {shouldShowLevelLegend({
              packages,
              services,
              isServiceOnly,
              hasContract: hasContractBanner(referralData ?? { code: QWIData?.referralSourceCode })
            }) && <CalibrationLevelLegend />}
            {/* Legend for the auto-quote marker — only shown when this quote carries lines mapped
                by ADAS Intelligence (source === 'AUTO_QUOTE'). Explains the trailing asterisk that
                ItemRow / MobileItemRow append to those calibration & service lines. */}
            {[...(packages || []), ...(services || [])].some((i) => i?.source === 'AUTO_QUOTE') && (
              <AdasBox className='mb-3 flex items-center gap-1.5 text-[12px] text-[#6B7280]'>
                <span className='font-semibold text-[#1F6FEB]'>*</span>
                <span>Auto-selected from the ADAS Intelligence report</span>
              </AdasBox>
            )}
            {!isServiceOnly && (
              <>
                <div className='hidden min-[435px]:block'>
                  <ExpandableSection
                    title='Calibrations'
                    total={packagesTotal}
                    items={packages}
                    manageLabel='MANAGE CALIBRATIONS'
                    onManage={() => handleManageClick('calibrations')}
                    disabled={!canEdit}
                    type={'calibration'}
                    onEdit={handleClickEditPricing}
                    isNational={isNationalAgreement}
                  />
                </div>
                <div className='min-[435px]:hidden'>
                  <MobileSection
                    title='Calibrations'
                    total={packagesTotal}
                    items={packages}
                    manageLabel='MANAGE CALIBRATIONS'
                    onManage={() => handleManageClick('calibrations')}
                    disabled={!canEdit}
                    type='calibration'
                    onEdit={handleClickEditPricing}
                    isNational={isNationalAgreement}
                  />
                </div>
                <EditProductPricing
                  key='edit calibration'
                  type='calibration'
                  title='Calibrations'
                  product={selectedProduct}
                  descriptionLength={MAX_DESCRIPTION_LENGTH}
                  isOpen={isOpenEditProduct.calibration}
                  onClose={handleClickClosePricing}
                  handleInputChange={handleEditPricingInputChange}
                  onSubmit={handleEditPricingSubmit}
                  isNational={QWIData?.referralSourceCode}
                />
              </>
            )}
            <div className='hidden min-[435px]:block'>
              <ExpandableSection
                title='Services'
                total={servicesTotal}
                items={services}
                manageLabel='MANAGE SERVICES'
                onManage={() => handleManageClick('services')}
                disabled={!canEdit}
                type={'service'}
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <div className='min-[435px]:hidden'>
              <MobileSection
                title='Services'
                total={servicesTotal}
                items={services}
                manageLabel='MANAGE SERVICES'
                onManage={() => handleManageClick('services')}
                disabled={!canEdit}
                type='service'
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <EditProductPricing
              key='edit service'
              type='service'
              title='Services'
              product={selectedProduct}
              descriptionLength={MAX_DESCRIPTION_LENGTH}
              isOpen={isOpenEditProduct.service}
              onClose={handleClickClosePricing}
              handleInputChange={handleEditPricingInputChange}
              onSubmit={handleEditPricingSubmit}
              isNational={QWIData?.referralSourceCode}
            />
            <div className='hidden min-[435px]:block'>
              <ExpandableSection
                key='Parts'
                title='Parts'
                total={partsTotal}
                items={parts}
                manageLabel='MANAGE PARTS'
                onManage={() => handleManageClick('parts')}
                type={'part'}
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <div className='min-[435px]:hidden'>
              <MobileSection
                title='Parts'
                total={partsTotal}
                items={parts}
                manageLabel='MANAGE PARTS'
                onManage={() => handleManageClick('parts')}
                disabled={!canEdit}
                defaultExpanded={false}
                type='part'
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <EditProductPricing
              key='edit part'
              type='part'
              title='Parts'
              product={selectedProduct}
              descriptionLength={MAX_DESCRIPTION_LENGTH}
              isOpen={isOpenEditProduct.part}
              onClose={handleClickClosePricing}
              handleInputChange={handleEditPricingInputChange}
              onSubmit={handleEditPricingSubmit}
              isNational={QWIData?.referralSourceCode}
            />
            <div className='hidden min-[435px]:block'>
              <ExpandableSection
                key='Labour'
                title='Labor'
                total={laborTotal}
                items={labor}
                manageLabel='MANAGE LABOR'
                onManage={() => handleManageClick('labor')}
                type={'labor'}
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <div className='min-[435px]:hidden'>
              <MobileSection
                title='Labor'
                total={laborTotal}
                items={labor}
                manageLabel='MANAGE LABOR'
                onManage={() => handleManageClick('labor')}
                disabled={!canEdit}
                defaultExpanded={false}
                type='labor'
                onEdit={handleClickEditPricing}
                isNational={isNationalAgreement}
              />
            </div>
            <EditProductPricing
              key='edit labor'
              type='labor'
              title='Labor'
              product={selectedProduct}
              descriptionLength={MAX_DESCRIPTION_LENGTH}
              isOpen={isOpenEditProduct.labor}
              onClose={handleClickClosePricing}
              handleInputChange={handleEditPricingInputChange}
              onSubmit={handleEditPricingSubmit}
              isNational={QWIData?.referralSourceCode}
            />
            <CustomItem
              type='quotes'
              customItemTotal={customItemTotal}
              existingCustomItemList={customItemList}
              workOrderData={quoteData}
              onSaveCustomItem={onSaveCustomItem}
              disabled={!canEdit}
            />
            <AdasBox className='mt-4 hidden min-[435px]:flex sticky bottom-3 z-20 items-center justify-between rounded-lg border border-[#D1D5D8] bg-white px-4 py-3 shadow-md'>
              <AdasTypography className='text-[14px] font-semibold uppercase tracking-wide text-[#2C3D4D]'>Total Amount</AdasTypography>
              <AdasTypography className='text-[20px] font-bold text-[#0A1628]'>{currencyFormatter(totalCost)}</AdasTypography>
            </AdasBox>

            <Note
              existingExternalNote={quoteInputValues.notes_external}
              existingInternalNote={quoteInputValues.notes_internal}
              existingClientNote={quoteInputValues.client_notes}
              submit={(props) => saveNote(props)}
              allowEdit={isActive}
            />

            {/* Sub-page nav strip — Vehicle Info / Documents (matches the Work Order summary
                pattern; quotes have no Photos sub-page so it's omitted). */}
            <AdasBox className='mt-4 flex flex-nowrap items-center gap-2 overflow-x-auto'>
              <button
                type='button'
                onClick={() => history.push(getUrlByNameAndId(REDIRECT_URLS.QUOTE_VEHICLE_INFO, referenceNumber))}
                className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
              >
                <Car className='h-4 w-4 shrink-0' />
                Vehicle Info
              </button>
              <button
                type='button'
                onClick={() => history.push(getUrlByNameAndId(REDIRECT_URLS.QUOTE_DOCUMENT, referenceNumber))}
                className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
              >
                <FileText className='h-4 w-4 shrink-0' />
                Documents
              </button>
            </AdasBox>
          </AdasBox>

          {/* Mobile floating bottom action buttons */}
          <AdasBox className='min-[435px]:hidden fixed bottom-5 left-3 right-3 z-30 flex items-center gap-3'>
            <button
              type='button'
              onClick={handleOpen}
              disabled={!canEdit}
              className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#D1D5D8] bg-white px-4 py-3 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] shadow-lg disabled:cursor-not-allowed disabled:opacity-40'
            >
              <CheckCircle2 className='h-4 w-4' />
              Close
            </button>
            {canConvertToWorkOrder && (
              <button
                type='button'
                onClick={convertToWO}
                disabled={!canEdit}
                className='inline-flex flex-[1.4] items-center justify-center gap-1.5 rounded-full bg-[#0A1628] px-4 py-3 text-[13px] font-bold uppercase tracking-wide text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-40'
              >
                <Wrench className='h-4 w-4' />
                Create Work Order
              </button>
            )}
          </AdasBox>
          {/* Spacer below the page content so the floating action bar doesn't cover the last section */}
          <AdasBox className='min-[435px]:hidden h-[88px]' aria-hidden='true' />

          {/* Bottom action footer — back to list on the left, Close Quote + Create Work Order on the right */}
          <AdasBox className='mt-6 hidden min-[435px]:flex flex-wrap items-center justify-between gap-3 border-t border-[#EBEDF0] pt-4'>
            <button
              type='button'
              onClick={() => history.goBack()}
              className='inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] hover:underline'
            >
              <ArrowLeft className='h-4 w-4' />
              Quotes
            </button>
            <AdasBox className='flex items-center gap-3'>
              <button
                type='button'
                onClick={handleOpen}
                disabled={!canEdit}
                className='inline-flex items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
              >
                <CheckCircle2 className='h-4 w-4' />
                Close Quote
              </button>
              {canConvertToWorkOrder && (
                <button
                  type='button'
                  onClick={convertToWO}
                  disabled={!canEdit}
                  className='inline-flex items-center gap-2 rounded-lg bg-[#0A1628] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <Wrench className='h-4 w-4' />
                  Create Work Order
                </button>
              )}
            </AdasBox>
          </AdasBox>

          <AdasBox>
            <BrandedModal open={open} onClose={handleClose} title='Close Quote?' width='w-[480px]'>
              <AdasTypography className='mb-4 text-[14px] text-[#2C3D4D]'>
                To confirm that you want to close this quote, type <span className='font-semibold text-[#0A1628]'>CLOSE</span> below.
              </AdasTypography>
              <AdasTextField
                onChange={(e) => setCloseInput(e.target.value.toUpperCase())}
                name='closeInput'
                id='closeInput'
                label='Confirm'
                variant='outlined'
                value={closeInput}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <AdasBox className='mt-5 flex items-center justify-end gap-3'>
                <AdasButton variant='outlined' onClick={handleClose} className='rounded-lg px-5'>
                  CANCEL
                </AdasButton>
                <AdasButton
                  variant='contained'
                  disabled={closeInput !== 'CLOSE'}
                  onClick={handleConfirmClose}
                  className='rounded-lg bg-[#0A1628] px-5 text-white hover:bg-[#1F2D3D]'
                >
                  CLOSE QUOTE
                </AdasButton>
              </AdasBox>
            </BrandedModal>
            <AdasModal
              open={isVinEdit}
              onClose={onCloseModal}
              aria-labelledby='modal-modal-title'
              aria-describedby='modal-modal-description'
              className='flex items-center justify-center'
            >
              <AdasPaper variant='outlined' className='pb-[20px] pr-[20px] pl-[20px]'>
                <h4>Convert Quote to Work Order</h4>
                <p>A 17 digit VIN is required to create a work order for the vehicle</p>
                <VinInput updateVehicleInfo={updateVehicleInfo} vinExistCheck={true} />
                {updatedVehicle && (
                  <AdasAlert
                    severity='success'
                    // color={theme.palette.background.highlight}
                    className='bg-[#96F7B8] my-[6px] py-0 rounded-sm'
                  >
                    {`${updatedVehicle.year} ${updatedVehicle.make} ${updatedVehicle.model}`} - Confirmed
                  </AdasAlert>
                )}
                {isVinInvalid && (
                  <AdasAlert
                    severity='error'
                    // color={theme.palette.background.highlight}
                    className='my-1 rounded-sm'
                  >
                    VIN not found!
                  </AdasAlert>
                )}
                <AdasBox className='flex justify-between mt-[10px]'>
                  <AdasButton className='ml-auto' onClick={onCloseModal}>
                    CANCEL
                  </AdasButton>
                  <AdasButton
                    variant='contained'
                    disabled={updatedVehicle && updatedVehicle.vin ? false : true}
                    isDebounceEnabled={true}
                    onClick={convertToWoUpdateVin}
                  >
                    CREATE WORK ORDER
                  </AdasButton>
                </AdasBox>
              </AdasPaper>
            </AdasModal>
          </AdasBox>

          <CreateSupportRequestModal open={supportOpen} onClose={() => setSupportOpen(false)} referenceNumber={referenceNumber} />

          {existingAppointmentDialog}

          {/* CAR-3678 — New Appointment, prefilled + linked to this quote */}
          {apptOpen && (
            <CreateAppointmentForm
              open={apptOpen}
              onClose={() => setApptOpen(false)}
              prefill={buildAppointmentPrefillFromQuote(quoteData)}
              linkedQuoteId={quoteData.id}
              linkedQwiId={QWIData?.id}
              onSaved={() => setQuoteData((prev) => ({ ...prev, sub_status: SubStatusEnum.SCHEDULED.value }))}
            />
          )}
        </AdasBox>
      )}
    </AdasContainer>
  )
}
